/**
 * Admin outreach mailbox pool + suppressions (Go /admin/outreach/*, scope
 * outreach:manage — admin tier only).
 *
 * getPool() is the feature probe: it is the one route the BE registers even
 * when the outreach config groups are unset, so `enabled:false` means "this
 * environment has no outreach", not "this build is broken".
 */
import { apiClient } from '@discovr/common';
import type { DataResponse, PaginatedResponse } from '@discovr/common';

export type OutreachMailboxStatus = 'warming' | 'active' | 'paused' | 'resync' | 'disabled';
export type OutreachSuppressionReason = 'hard_bounce' | 'complaint' | 'unsubscribe' | 'manual';
export type OutreachSuppressionScope = 'global' | 'brand' | 'campaign';
export type OutreachUnmatchedConfidence = 'address' | 'headers' | 'unmatched';

export interface OutreachPoolCapability {
  enabled: boolean;
  /** Present only when enabled. The addresses the "Add mailbox" form may offer. */
  pool_addresses?: string[];
  /** Non-disabled mailboxes — the Workspace seats in use. */
  mailbox_count?: number;
  active_count?: number;
  /** True only for a caller holding outreach:mailbox:add (Super Admin). */
  can_add_mailbox?: boolean;
  allowed_domains?: string[];
  max_per_domain?: number;
  /** Public client ID of the Discovr service account, for domain-wide delegation. */
  service_account_client_id?: string;
  delegation_scopes?: string[];
}

export interface OutreachMailboxCheck {
  address: string;
  domain: string;
  delegated: boolean;
  slots_left: number;
}

export interface OutreachMailbox {
  id: number;
  address: string;
  domain: string;
  display_name: string;
  signature?: string;
  /** The signature's formatting; absent for a signature written before formatting existed. */
  signature_html?: string;
  status: OutreachMailboxStatus;
  /** The policy number an admin edits. */
  daily_cap: number;
  /** The warm-up rung: the highest daily_cap a PATCH will accept right now. */
  allowed_cap: number;
  /** The enforcement numbers, for today in OUTREACH_CAPS_TZ. */
  sent_today: number;
  cap_today: number;
  sends_7d: number;
  hard_bounces_7d: number;
  /** Delegation proven at least once; the ramp's promotion condition. */
  verified: boolean;
  warmup_started_at?: string;
  paused_reason?: string;
  last_polled_at?: string;
  last_successful_poll_at?: string;
  last_poll_error?: string;
  /** Mirrors the stale-poll SLO independent of status (2.8 late intake): a
   *  mailbox can read status "active" while every poll has been failing.
   *  Optional: a legacy row or a BE not yet carrying the field omits it, and
   *  that must read as healthy, not unhealthy (see isPollUnhealthy). */
  poll_healthy?: boolean;
  /** The latest test send from this mailbox (2.8 Task 18). */
  last_test_send_at?: string;
  policy_rejections_7d: number;
  /** The sending domain's latest Postmaster grade (send guards P2). A warn steps cap_today down; a hold pauses the domain's mailboxes.
   *  Optional: an older backend omits it, which reads as ok. */
  domain_guard_level?: 'ok' | 'warn' | 'hold';
  domain_guard_reason?: string;
  /** The grade is ok again but the domain's cap factor is still climbing back to 1 after a warn; cap_today is still reduced. */
  recovering?: boolean;
  /** That factor (0 to 1), present only while recovering. */
  domain_cap_factor?: number;
  created_by: string;
  created_at: string;
}

/** A ProjectEntry job River gave up on — discarded after repeated failures,
 *  or cancelled because a fact could not be folded at all (2.8 late intake:
 *  Settings → Outreach → Diagnostics). */
export interface QuarantinedProjectionJob {
  job_id: number;
  entry_id: string;
  state: 'discarded' | 'cancelled';
  attempt: number;
  attempted_at: string;
  last_error: string;
}

export interface OutreachUnmatchedMessage {
  id: number; mailbox_id: number; mailbox_address: string; from: string; from_address: string; subject?: string; snippet?: string;
  received_at: string; match_confidence: OutreachUnmatchedConfidence; candidate_entry_id?: string; campaign_id?: string;
  is_auto_reply: boolean; is_dsn: boolean; parse_error?: string; triage_escalated_at?: string; candidate_reason?: string;
  dismissed_at?: string; dismissed_by?: string;
}

export type OutreachBounceRefusalReason =
  | 'bounce_unauthenticated' | 'bounce_unbound' | 'bounce_mailbox_mismatch'
  | 'bounce_recipient_mismatch' | 'bounce_stale' | 'bounce_unreadable';

/** Present only for a bounce report; `reason` only when Go refused it. */
export interface OutreachAttachBounce {
  applied: boolean;
  kind: 'hard' | 'soft' | 'blocked' | 'delayed' | 'other';
  reason?: OutreachBounceRefusalReason | (string & {});
}

export interface OutreachAttachResult {
  message_id: number; entry_id: string; outcome: string; bounce?: OutreachAttachBounce;
}

export interface OutreachSuppression {
  /** Surrogate key (0007); the only key the remove route accepts, so the
   *  address never lands in a URL or request log (E2E M7). */
  id: number;
  address_hash: string;
  /** Absent once the creator has been erased; address_hash still blocks contact. */
  address?: string;
  erased: boolean;
  reason: OutreachSuppressionReason;
  source: string;
  campaign_id?: string;
  mailbox_id?: number;
  created_by?: string;
  created_at: string;
  scope: OutreachSuppressionScope;
  scope_brand_id?: string;
  scope_campaign_id?: string;
  /** Resolved by the BE (2.8 Task 3); absent when the brand/campaign no longer exists. */
  scope_brand_name?: string;
  scope_campaign_name?: string;
  /** Set only on a brand/campaign-scoped row created with a pause window. */
  expires_at?: string;
}

/** One match in the suppression dialog's brand/campaign picker. */
/** Outreach settings that are not per campaign (Settings → Outreach → General). */
export interface OutreachGlobalSettings {
  /** Printed in the opt-out footer of mail to creators in the US. Empty holds that mail. */
  us_postal_address: string;
  updated_at?: string;
  updated_by?: string;
}

/** The save response: how many held US mails went back in the queue. Absent from an older backend. */
export interface OutreachGlobalSettingsSaved extends OutreachGlobalSettings {
  released?: number;
}

export interface OutreachScopeOption {
  id: string;
  name: string;
  /** Campaign matches carry their brand's name. */
  hint?: string;
}

const SCOPE_TARGETS_PATH = '/admin/outreach/scope-targets';

interface OutreachScopeTarget {
  id: string;
  name: string;
  brand_name?: string;
}

const MAILBOXES = '/admin/outreach/mailboxes';
const SUPPRESSIONS = '/admin/outreach/suppressions';
const UNMATCHED_MESSAGES = '/admin/outreach/unmatched-messages';
const ERASURE = '/admin/outreach/erasure';
const PROJECTION_QUARANTINE = '/admin/outreach/projection/quarantine';
const GLOBAL_SETTINGS = '/admin/outreach/settings';
const TEST_RECIPIENTS = '/admin/outreach/test-recipients';

/** An address an admin added to the non-prod send allowlist. */
export interface OutreachTestRecipient {
  address: string;
  added_by: string;
  created_at: string;
}

/** Base = set in infra, read-only; extras = added from the admin app. */
export interface OutreachTestRecipients {
  base_mailboxes: string[];
  base_domains: string[];
  extras: OutreachTestRecipient[];
}

const action = (id: number, verb: string, body?: unknown): Promise<OutreachMailbox> =>
  apiClient
    .post<DataResponse<OutreachMailbox>>(`${MAILBOXES}/${id}/${verb}`, body)
    .then((r) => r.data.data);

export const outreachAdmin = {
  getPool: (): Promise<OutreachPoolCapability> =>
    apiClient.get<DataResponse<OutreachPoolCapability>>('/admin/outreach/pool').then((r) => r.data.data),

  listMailboxes: (): Promise<OutreachMailbox[]> =>
    apiClient.get<DataResponse<OutreachMailbox[]>>(MAILBOXES).then((r) => r.data.data),

  /** signature_html is the signature: the BE derives its text and drops links. '' clears it. */
  createMailbox: (body: { address: string; display_name: string; signature_html?: string }): Promise<OutreachMailbox> =>
    apiClient.post<DataResponse<OutreachMailbox>>(MAILBOXES, body).then((r) => r.data.data),

  /** Dry run: every add rule and the Workspace delegation probe, nothing saved. */
  checkMailbox: (address: string): Promise<OutreachMailboxCheck> =>
    apiClient.post<DataResponse<OutreachMailboxCheck>>(`${MAILBOXES}/check`, { address }).then((r) => r.data.data),

  getMailboxHealth: (id: number): Promise<OutreachMailbox> =>
    apiClient.get<DataResponse<OutreachMailbox>>(`${MAILBOXES}/${id}/health`).then((r) => r.data.data),

  updateMailbox: (id: number, body: { display_name?: string; signature_html?: string; daily_cap?: number }): Promise<OutreachMailbox> =>
    apiClient.patch<DataResponse<OutreachMailbox>>(`${MAILBOXES}/${id}`, body).then((r) => r.data.data),

  pauseMailbox: (id: number, reason?: string): Promise<OutreachMailbox> => action(id, 'pause', { reason }),
  resumeMailbox: (id: number): Promise<OutreachMailbox> => action(id, 'resume'),
  disableMailbox: (id: number): Promise<OutreachMailbox> => action(id, 'disable'),
  verifyMailbox: (id: number): Promise<OutreachMailbox> => action(id, 'verify'),

  /** Sends a test email from a pool mailbox to the CALLER's own inbox. The BE
   *  requires an Idempotency-Key so a double-click can never queue two sends. */
  testSend: (from: string): Promise<{ job_id: number; to: string }> =>
    apiClient
      .post<DataResponse<{ job_id: number; to: string }>>('/admin/outreach/pool/test-send', { from }, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      .then((r) => r.data.data),

  listSuppressions: (params?: { q?: string; reason?: string; scope?: OutreachSuppressionScope; page?: number; limit?: number }): Promise<PaginatedResponse<OutreachSuppression>> =>
    apiClient.get<PaginatedResponse<OutreachSuppression>>(SUPPRESSIONS, { params }).then((r) => r.data),

  // Admin add is manual-only; the BE answers 422 INVALID_REASON for any other reason.
  addSuppression: (body: { address: string; reason?: 'manual'; campaign_id?: string; scope?: OutreachSuppressionScope; scope_brand_id?: string; scope_campaign_id?: string }): Promise<OutreachSuppression> =>
    apiClient.post<DataResponse<OutreachSuppression>>(SUPPRESSIONS, body).then((r) => r.data.data),

  searchScopeBrands: (q: string): Promise<OutreachScopeOption[]> =>
    apiClient
      .get<{ data: OutreachScopeTarget[] | null }>(SCOPE_TARGETS_PATH, { params: { kind: 'brand', q } })
      .then((r) => (r.data.data ?? []).map((b) => ({ id: b.id, name: b.name }))),

  searchScopeCampaigns: (q: string): Promise<OutreachScopeOption[]> =>
    apiClient
      .get<{ data: OutreachScopeTarget[] | null }>(SCOPE_TARGETS_PATH, { params: { kind: 'campaign', q } })
      .then((r) => (r.data.data ?? []).map((c) => ({ id: c.id, name: c.name, hint: c.brand_name }))),

  removeSuppression: (id: number): Promise<void> => {
    if (!Number.isInteger(id) || id <= 0) return Promise.reject(new Error('A suppression id is required.'));
    return apiClient.delete(`${SUPPRESSIONS}/${id}`).then(() => undefined);
  },

  listUnmatched: (params?: { page?: number; limit?: number; mailbox_id?: number; confidence?: OutreachUnmatchedConfidence; dismissed?: boolean }): Promise<PaginatedResponse<OutreachUnmatchedMessage>> => {
    const defined = {
      ...(params?.page !== undefined ? { page: params.page } : {}),
      ...(params?.limit !== undefined ? { limit: params.limit } : {}),
      ...(params?.mailbox_id !== undefined ? { mailbox_id: params.mailbox_id } : {}),
      ...(params?.confidence !== undefined ? { confidence: params.confidence } : {}),
      ...(params?.dismissed ? { dismissed: true } : {}),
    };
    return apiClient.get<PaginatedResponse<OutreachUnmatchedMessage>>(UNMATCHED_MESSAGES, { params: defined }).then((r) => r.data);
  },
  attachUnmatched: (messageId: number, entryId: string): Promise<OutreachAttachResult> =>
    apiClient.post<DataResponse<OutreachAttachResult>>(`${UNMATCHED_MESSAGES}/${messageId}/attach`, { entry_id: entryId }).then((r) => r.data.data),
  /** Takes an unmatched message off the list and the badge; the row is kept. */
  dismissUnmatched: (messageId: number): Promise<void> =>
    apiClient.post(`${UNMATCHED_MESSAGES}/${messageId}/dismiss`).then(() => undefined),
  /** R17-65: puts a dismissed message back on the list (until the 90-day clean-up). */
  restoreUnmatched: (messageId: number): Promise<void> =>
    apiClient.post(`${UNMATCHED_MESSAGES}/${messageId}/restore`).then(() => undefined),
  resyncMailbox: (id: number): Promise<OutreachMailbox> => action(id, 'resync'),

  /** Ruling 65 — GDPR-style erasure, admin-only. `idempotencyKey` is the
   *  caller's own responsibility (a fresh key per submit) so a double-click
   *  never starts two erasure operations for the same creator. */
  requestOutreachErasure: (creatorUserId: string, idempotencyKey: string): Promise<{ erasureId: string }> =>
    apiClient
      .post<DataResponse<{ erasure_id: string }>>(
        ERASURE,
        { creator_user_id: creatorUserId },
        { headers: { 'Idempotency-Key': idempotencyKey } },
      )
      .then((r) => ({ erasureId: r.data.data.erasure_id })),

  getGlobalSettings: (): Promise<OutreachGlobalSettings> =>
    apiClient.get<DataResponse<OutreachGlobalSettings>>(GLOBAL_SETTINGS).then((r) => r.data.data),

  putGlobalSettings: (usPostalAddress: string): Promise<OutreachGlobalSettingsSaved> =>
    apiClient.put<DataResponse<OutreachGlobalSettingsSaved>>(GLOBAL_SETTINGS, { us_postal_address: usPostalAddress }).then((r) => r.data.data),

  // TEMPORARY, non-prod only: the send guard's test recipients. The BE answers 404 where the guard is off (prod).
  listTestRecipients: (): Promise<OutreachTestRecipients> =>
    apiClient.get<DataResponse<OutreachTestRecipients>>(TEST_RECIPIENTS).then((r) => r.data.data),

  addTestRecipient: (address: string): Promise<OutreachTestRecipient> =>
    apiClient.post<DataResponse<OutreachTestRecipient>>(TEST_RECIPIENTS, { address }).then((r) => r.data.data),

  removeTestRecipient: (address: string): Promise<void> =>
    apiClient.delete(TEST_RECIPIENTS, { params: { address } }).then(() => undefined),

  listProjectionQuarantine: (): Promise<QuarantinedProjectionJob[]> =>
    apiClient.get<PaginatedResponse<QuarantinedProjectionJob>>(PROJECTION_QUARANTINE).then((r) => r.data.data),

  replayProjectionQuarantine: (jobId: number): Promise<{ job_id: number }> =>
    apiClient.post<DataResponse<{ job_id: number }>>(`${PROJECTION_QUARANTINE}/${jobId}/replay`).then((r) => r.data.data),
};
